import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './BlackList.css';
import { firestore } from '../firebaseConfig';
import {
  collection, onSnapshot, doc, getDoc, setDoc, deleteDoc, query, orderBy,
  getDocs, writeBatch, where, updateDoc, limit, getCountFromServer, documentId,
} from 'firebase/firestore';
import { CONSUMER_EMAIL_PROVIDERS } from '../shared/enums';

const STANDARD_CONSUMER_PROVIDERS = CONSUMER_EMAIL_PROVIDERS;
// Fast O(1) lookup for "is this domain a consumer provider?".
// Used to keep Gmail/GMX/Yahoo etc. reachable even when the user picked
// domain-level import — otherwise a single Gmail in the CSV would block
// every Gmail address on the planet.
const CONSUMER_PROVIDER_SET = new Set(CONSUMER_EMAIL_PROVIDERS);

// Initial page size shown on mount. With 30k+ entries the previous "load
// everything" approach pulled all docs on every Open; now we paginate.
const PAGE_SIZE = 20;
const SEARCH_LIMIT = 50;
// Firestore "in" queries are capped at 30 array members.
const IN_QUERY_CHUNK = 30;

// Firestore Doc-ID rules: cannot contain "/", cannot be ".", "..", cannot match
// __.*__, must be <=1500 bytes UTF-8, no control chars / newlines.
const FORBIDDEN_DOC_ID_RE = /[\u0000-\u001F\u007F/]/;
const isValidBlacklistDocId = (entry) => {
  if (!entry || typeof entry !== 'string') return false;
  if (entry === '.' || entry === '..') return false;
  if (FORBIDDEN_DOC_ID_RE.test(entry)) return false;
  if (/^__.*__$/.test(entry)) return false;
  // 1500 bytes UTF-8 limit
  if (new Blob([entry]).size > 1500) return false;
  return true;
};

// Run async tasks with a concurrency cap. Keeps memory + Firestore load bounded.
const runWithConcurrency = async (items, concurrency, worker) => {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (cursor < items.length) {
      const idx = cursor++;
      results[idx] = await worker(items[idx], idx);
    }
  });
  await Promise.all(runners);
  return results;
};

const sourceLabel = (source) => {
  if (source === 'manual') return 'Manual';
  if (source === 'standard_providers') return 'Standard';
  if (source === 'file_import') return 'Import';
  if (source === 'smart_inbox') return 'Inbox';
  if (source === 'footer_optout' || source === 'list_unsubscribe') return "I don't want to receive more emails";
  return source;
};

const docToEntry = (d) => {
  const data = d.data();
  return {
    id: d.id,
    email: data.email || d.id,
    addedAt: data.addedAt,
    source: data.source,
    businessName: data.businessName,
    contactId: data.contactId,
    type: data.type || (data.email?.startsWith('@') ? 'domain' : 'email'),
  };
};

const BlackList = ({ userId }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [columns, setColumns] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [step, setStep] = useState(1); // 1: File selection, 2: Column mapping, 3: Success
  const [delimiter, setDelimiter] = useState(';');
  const [emailColumnIndex, setEmailColumnIndex] = useState(null);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState('');
  // Paginated entries (most recent first). Grows when user clicks "Load more".
  const [pagedEntries, setPagedEntries] = useState([]);
  const [pageLimit, setPageLimit] = useState(PAGE_SIZE);
  // Aggregate counts via getCountFromServer (1 read each, not 30k).
  const [counts, setCounts] = useState({ total: 0, domains: 0, emails: 0 });
  // Server-side search results. `null` means "not in search mode".
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  // All consumer-provider entries that exist on the blacklist (regardless of
  // source). Drives both the warning banner and the Quick-Block modal.
  const [allConsumerBlocks, setAllConsumerBlocks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [blockType, setBlockType] = useState('email'); // 'email' oder 'domain'
  const [importBlockType, setImportBlockType] = useState('email'); // 'email' oder 'domain' für CSV-Import
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [addingProviders, setAddingProviders] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [clearing, setClearing] = useState(false);
  const fileInputRef = useRef(null);
  // Pause the live snapshot during bulk mutations to avoid re-fetching the
  // page repeatedly while we're committing.
  const pauseSnapshotRef = useRef(false);
  const resubscribeRef = useRef(null);

  // Refresh aggregate counts + the list of consumer-provider blocks.
  // Cheap: 3 count queries + ~2 'in' queries (≤30 docs each).
  const refreshMeta = useCallback(async () => {
    if (!userId) return;
    try {
      const colRef = collection(firestore, `World/${userId}/blacklist`);
      const [totalSnap, domainSnap, emailSnap] = await Promise.all([
        getCountFromServer(colRef),
        getCountFromServer(query(colRef, where('type', '==', 'domain'))),
        getCountFromServer(query(colRef, where('type', '==', 'email'))),
      ]);
      setCounts({
        total: totalSnap.data().count,
        domains: domainSnap.data().count,
        emails: emailSnap.data().count,
      });

      // Consumer-provider lookup, chunked into 'in'-query batches.
      const chunks = [];
      for (let i = 0; i < STANDARD_CONSUMER_PROVIDERS.length; i += IN_QUERY_CHUNK) {
        chunks.push(STANDARD_CONSUMER_PROVIDERS.slice(i, i + IN_QUERY_CHUNK));
      }
      const consumerSnaps = await Promise.all(
        chunks.map(chunk => getDocs(query(colRef, where(documentId(), 'in', chunk))))
      );
      const consumerDocs = [];
      consumerSnaps.forEach(snap => snap.docs.forEach(d => consumerDocs.push(docToEntry(d))));
      setAllConsumerBlocks(consumerDocs);
    } catch (e) {
      console.error('[Blacklist] refreshMeta error:', e);
    }
  }, [userId]);

  // Load the first page (or current pageLimit) live. Only the most recent
  // PAGE_SIZE entries are pulled — switching to a larger limit is opt-in via
  // the "Load more" button.
  useEffect(() => {
    if (!userId) return;

    const colRef = collection(firestore, `World/${userId}/blacklist`);
    const q = query(colRef, orderBy('addedAt', 'desc'), limit(pageLimit));

    let unsubscribe = null;
    const subscribe = () => {
      unsubscribe = onSnapshot(q, (snapshot) => {
        if (pauseSnapshotRef.current) return;
        setPagedEntries(snapshot.docs.map(docToEntry));
      });
    };

    subscribe();
    resubscribeRef.current = () => {
      if (unsubscribe) unsubscribe();
      subscribe();
    };

    return () => {
      if (unsubscribe) unsubscribe();
      resubscribeRef.current = null;
    };
  }, [userId, pageLimit]);

  // Refresh meta on mount + whenever userId changes.
  useEffect(() => { refreshMeta(); }, [refreshMeta]);

  // Debounced server-side search. Prefix match on doc-ID (entries are stored
  // with the email/domain as their ID). Empty input → exits search mode and
  // we show the paginated list again.
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    if (!userId) return;
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const colRef = collection(firestore, `World/${userId}/blacklist`);
        // Firestore only supports prefix matching on document IDs. To make
        // "tui" find both "tuija@…" (emails) AND "@tui.de" (domains) we run
        // two prefix queries in parallel: one for the term as typed, one for
        // "@" + term. When the user already typed "@" we only need one query.
        const prefixes = term.startsWith('@') ? [term] : [term, '@' + term];
        const buildPrefixQuery = (prefix) => query(
          colRef,
          orderBy(documentId()),
          where(documentId(), '>=', prefix),
          where(documentId(), '<', prefix + '\uf8ff'),
          limit(SEARCH_LIMIT),
        );
        const snaps = await Promise.all(prefixes.map(p => getDocs(buildPrefixQuery(p))));
        // Merge + dedup (no overlap is possible between "x" and "@x" prefixes
        // since IDs starting with "@" can't also start with "x", but we dedup
        // defensively in case of future changes). Cap at SEARCH_LIMIT total
        // and prefer domain hits first so they don't get truncated away.
        const seen = new Set();
        const merged = [];
        const ordered = term.startsWith('@') ? snaps : [snaps[1], snaps[0]];
        for (const snap of ordered) {
          for (const d of snap.docs) {
            if (seen.has(d.id)) continue;
            seen.add(d.id);
            merged.push(docToEntry(d));
            if (merged.length >= SEARCH_LIMIT) break;
          }
          if (merged.length >= SEARCH_LIMIT) break;
        }
        setSearchResults(merged);
      } catch (e) {
        console.error('[Blacklist] search error:', e);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [searchTerm, userId]);

  // Derived: only the consumer-provider blocks that look unintentional.
  // Quick-Block-modal entries (`source === 'standard_providers'`) are excluded
  // — those were created on purpose.
  const consumerDomainBlocks = useMemo(
    () => allConsumerBlocks.filter(b => b.source !== 'standard_providers'),
    [allConsumerBlocks]
  );

  // For the Quick-Block modal: which standard providers are *already* blocked
  // anywhere on the blacklist (regardless of source).
  const alreadyBlockedProviders = useMemo(
    () => allConsumerBlocks.map(b => b.email),
    [allConsumerBlocks]
  );
  const availableQuickBlockProviders = useMemo(
    () => {
      const blockedSet = new Set(alreadyBlockedProviders);
      return STANDARD_CONSUMER_PROVIDERS.filter(p => !blockedSet.has(p));
    },
    [alreadyBlockedProviders]
  );

  // Delimiter Detection
  const detectDelimiter = (text) => {
    const firstLine = text.split('\n')[0];
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    const commaCount = (firstLine.match(/,/g) || []).length;
    const tabCount = (firstLine.match(/\t/g) || []).length;
    
    if (tabCount > semicolonCount && tabCount > commaCount) return '\t';
    return semicolonCount >= commaCount ? ';' : ',';
  };

  // Parse CSV/TXT
  const parseFile = (text, delimiter) => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    
    if (lines.length === 0) {
      return { header: [], rowsData: [] };
    }

    const rows = [];
    
    for (let line of lines) {
      const cells = line.split(delimiter).map(cell => cell.trim());
      rows.push(cells);
    }

    const header = rows[0];
    const rowsData = rows.slice(1);

    return { header, rowsData };
  };

  // Pattern: full email like peter@firma.de
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Pattern: bare domain like @firma.de — used by domain-only blacklist exports.
  const BARE_DOMAIN_RE = /^@[^\s@]+\.[^\s@]+$/;
  // Pattern: bare hostname like firma.de or sub.firma.de — produced by CSVs
  // that list "Domain / URL" instead of email addresses.
  const HOSTNAME_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9-]+)*\.[a-z]{2,}$/i;

  // Normalize any importable cell value into the canonical blacklist form.
  // Returns:
  //  - "peter@firma.de" for full emails
  //  - "@firma.de" for bare domains, hostnames ("firma.de", "www.firma.de")
  //    and URLs ("https://firma.de/path?q=1")
  //  - null when the value is unusable
  const normalizeImportValue = (raw) => {
    if (!raw) return null;
    const v = String(raw).trim().toLowerCase();
    if (!v) return null;
    if (EMAIL_RE.test(v)) return v;
    if (BARE_DOMAIN_RE.test(v)) return v;
    // Strip protocol, www., path, query and trailing punctuation so we can
    // pull a clean hostname out of common spreadsheet exports.
    let host = v.replace(/^[a-z]+:\/\//, '');
    const sepIdx = host.search(/[/?#]/);
    if (sepIdx >= 0) host = host.substring(0, sepIdx);
    host = host.replace(/^www\./, '').replace(/[.,;:]+$/, '');
    if (HOSTNAME_RE.test(host)) return '@' + host;
    return null;
  };

  const isImportableValue = (v) => normalizeImportValue(v) !== null;

  // Automatische Spalten-Erkennung — akzeptiert volle E-Mails und bare Domains
  const detectEmailColumn = (header, rows) => {
    // 1. Versuche anhand des Header-Namens
    const headerIndex = header.findIndex(col => {
      const lowerCol = (col || '').toLowerCase();
      return (
        lowerCol.includes('email') ||
        lowerCol.includes('e-mail') ||
        lowerCol === 'mail' ||
        lowerCol === 'value' ||
        lowerCol === 'domain' ||
        lowerCol === 'address'
      );
    });

    if (headerIndex !== -1) {
      const sampleValues = rows.slice(0, 5).map(row => row[headerIndex]?.trim().toLowerCase());
      const validValues = sampleValues.filter(isImportableValue);
      if (validValues.length > 0) return headerIndex;
    }

    // 2. Fallback: Durchsuche alle Spalten nach Email-/Domain-Pattern
    for (let colIndex = 0; colIndex < header.length; colIndex++) {
      const sampleValues = rows.slice(0, 10).map(row => row[colIndex]?.trim().toLowerCase());
      const validValues = sampleValues.filter(isImportableValue);
      if (validValues.length >= sampleValues.length * 0.5) {
        return colIndex;
      }
    }

    return null;
  };

  // Datei Upload Handler
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Überprüfe Dateiendung
    const validExtensions = ['.csv', '.txt'];
    const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      setMessage('Please upload a CSV or TXT file.');
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        
        // Erkenne Delimiter
        const detectedDelimiter = detectDelimiter(text);
        setDelimiter(detectedDelimiter);
        
        // Parse die Datei
        const { header, rowsData } = parseFile(text, detectedDelimiter);
        
        if (header.length === 0) {
          setMessage('No valid data found in the file.');
          return;
        }
        
        setColumns(header);
        setCsvRows(rowsData);
        
        // Automatische Email-Spalten-Erkennung
        const detectedEmailColumn = detectEmailColumn(header, rowsData);
        setEmailColumnIndex(detectedEmailColumn);
        
        if (detectedEmailColumn !== null) {
          setMessage(`Email column detected: "${header[detectedEmailColumn]}"`);
        } else {
          setMessage('No email column detected. Please select manually.');
        }
        
        setStep(2); // Gehe zu Mapping-Schritt
      } catch (error) {
        console.error('Error parsing file:', error);
        setMessage('Error parsing file. Please check the format.');
      }
    };
    
    reader.readAsText(selectedFile);
  };

  // Extrahiere Domain aus E-Mail-Adresse
  const extractDomainFromEmail = (email) => {
    if (!email || typeof email !== 'string') return '';
    const atIndex = email.indexOf('@');
    if (atIndex === -1) return '';
    return email.substring(atIndex).toLowerCase(); // Gibt z.B. "@gmail.com" zurück
  };

  // Import Emails/Domains to Blacklist
  const handleImport = async () => {
    if (emailColumnIndex === null) {
      setMessage('Please select the email column first.');
      return;
    }

    setImporting(true);
    setImportProgress({ done: 0, total: 0 });
    const importTypeLabel = importBlockType === 'domain' ? 'domains' : 'emails';
    setMessage(`Preparing ${importTypeLabel}...`);

    try {
      // Split into two buckets so consumer providers (gmail/gmx/yahoo/…) are
      // always blocked per-address when the user picked domain-level import.
      // Otherwise one Gmail in the CSV would block ALL Gmail forever.
      const emailEntries = new Set();
      const domainEntries = new Set();
      let invalidIdCount = 0;
      let consumerRoutedCount = 0;
      let bareDomainCount = 0;

      for (let row of csvRows) {
        const rawVal = row[emailColumnIndex]?.trim().toLowerCase();
        if (!rawVal) continue;

        // Accept emails ("peter@firma.de"), bare domains ("@firma.de"),
        // hostnames ("firma.de") and URLs ("https://firma.de/path") — the
        // latter two get normalized to "@firma.de" so the rest of the import
        // flow can stay agnostic.
        const val = normalizeImportValue(rawVal);
        if (!val) continue;

        const isBareDomain = BARE_DOMAIN_RE.test(val);
        const isFullEmail = !isBareDomain && EMAIL_RE.test(val);
        if (!isBareDomain && !isFullEmail) continue;

        let entryToAdd;
        let target;

        if (isBareDomain) {
          // CSV row is already a domain (e.g. "@firma.de"). The user was
          // explicit; we always create a domain block, regardless of which
          // mode the toggle is in (you can't email-block a bare domain).
          // Consumer-safety net does NOT apply here — if the user typed
          // "@gmail.com" into their CSV, they meant it.
          entryToAdd = val;
          target = domainEntries;
          bareDomainCount++;
        } else {
          // Full email — apply mode + consumer-routing rules.
          const domain = extractDomainFromEmail(val);
          const isConsumer = domain && CONSUMER_PROVIDER_SET.has(domain);
          if (importBlockType === 'domain') {
            if (isConsumer) {
              entryToAdd = val;
              target = emailEntries;
              consumerRoutedCount++;
            } else {
              entryToAdd = domain;
              target = domainEntries;
            }
          } else {
            entryToAdd = val;
            target = emailEntries;
          }
        }

        if (!entryToAdd) continue;

        // Within-CSV dedup is cheap and avoids redundant batch writes.
        // We deliberately do NOT pre-check Firestore for existing entries —
        // doing so would require pulling the full collection (30k+ reads)
        // every time. `set()` is idempotent, so re-importing simply refreshes
        // the entry's metadata, which is what users expect anyway.
        if (target.has(entryToAdd)) continue;

        if (!isValidBlacklistDocId(entryToAdd)) {
          invalidIdCount++;
          continue;
        }

        target.add(entryToAdd);
      }

      const allEntries = [
        ...[...emailEntries].map(e => ({ entry: e, type: 'email' })),
        ...[...domainEntries].map(e => ({ entry: e, type: 'domain' })),
      ];
      const totalEntries = allEntries.length;
      const BATCH_SIZE = 500;
      const PARALLEL_BATCHES = 5;
      const baseTimestamp = Date.now();

      // Build chunks up-front so the worker just commits them.
      const chunks = [];
      for (let i = 0; i < totalEntries; i += BATCH_SIZE) {
        chunks.push({ start: i, items: allEntries.slice(i, i + BATCH_SIZE) });
      }

      setImportProgress({ done: 0, total: totalEntries });

      // Pause snapshot listener while we hammer Firestore — otherwise it
      // re-fetches the entire collection up to (chunks.length) times.
      pauseSnapshotRef.current = true;

      let committed = 0;
      try {
        await runWithConcurrency(chunks, PARALLEL_BATCHES, async ({ start, items }) => {
          const batch = writeBatch(firestore);
          items.forEach(({ entry, type }, idx) => {
            const blacklistDocRef = doc(firestore, `World/${userId}/blacklist`, entry);
            batch.set(blacklistDocRef, {
              email: entry,
              // Stable, unique ordering — avoids 50k ties on the same ms.
              addedAt: baseTimestamp + start + idx,
              source: 'file_import',
              type,
            });
          });
          await batch.commit();
          committed += items.length;
          setImportProgress({ done: committed, total: totalEntries });
        });
      } finally {
        pauseSnapshotRef.current = false;
        // Single fresh fetch instead of N intermediate snapshots.
        if (resubscribeRef.current) resubscribeRef.current();
        refreshMeta();
      }

      const parts = [];
      const dCount = domainEntries.size;
      const eCount = emailEntries.size;
      if (dCount > 0 && eCount > 0) {
        parts.push(`Imported ${dCount} domain block${dCount === 1 ? '' : 's'} and ${eCount} email${eCount === 1 ? '' : 's'} (from ${csvRows.length} rows).`);
      } else if (dCount > 0) {
        parts.push(`Imported ${dCount} domain block${dCount === 1 ? '' : 's'} (from ${csvRows.length} rows).`);
      } else if (eCount > 0) {
        parts.push(`Imported ${eCount} email${eCount === 1 ? '' : 's'} (from ${csvRows.length} rows).`);
      } else {
        parts.push(`No valid entries found in ${csvRows.length} rows.`);
      }
      if (consumerRoutedCount > 0) {
        parts.push(`${consumerRoutedCount} consumer-provider row${consumerRoutedCount === 1 ? '' : 's'} blocked per address — Gmail, GMX, Yahoo etc. stay reachable for everyone else.`);
      }
      if (bareDomainCount > 0 && importBlockType === 'email') {
        parts.push(`${bareDomainCount} bare-domain row${bareDomainCount === 1 ? '' : 's'} were blocked at domain level (cannot be email-blocked).`);
      }
      if (invalidIdCount > 0) parts.push(`${invalidIdCount} invalid entries skipped.`);
      setMessage(parts.join(' '));
      setStep(3);

      setTimeout(() => {
        setStep(1);
        setFile(null);
        setFileName('');
        setColumns([]);
        setCsvRows([]);
        setEmailColumnIndex(null);
        setImportBlockType('email');
        setImportProgress({ done: 0, total: 0 });
      }, 5000);

    } catch (error) {
      console.error('Error importing:', error);
      setMessage('Error importing. Please try again.');
      pauseSnapshotRef.current = false;
      if (resubscribeRef.current) resubscribeRef.current();
    } finally {
      setImporting(false);
    }
  };

  // Manuelle Email/Domain hinzufügen
  const handleAddManualEntry = async () => {
    let entry = manualEmail.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const domainRegex = /^@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/;
    
    if (!entry) {
      setMessage('Please enter an email address or domain.');
      return;
    }
    
    // Bestimme den Typ basierend auf Eingabe
    let entryType = blockType;
    
    // Wenn User eine Domain eingeben will aber @ vergessen hat
    if (blockType === 'domain' && !entry.startsWith('@')) {
      entry = '@' + entry;
    }
    
    // Automatische Typ-Erkennung
    if (entry.startsWith('@')) {
      entryType = 'domain';
      if (!domainRegex.test(entry)) {
        setMessage('Please enter a valid domain (e.g. @example.com).');
        return;
      }
      // Hard-block consumer providers as domain entries — that would
      // silently kill all outreach to Gmail / GMX / Yahoo etc.
      if (CONSUMER_PROVIDER_SET.has(entry)) {
        setMessage(`"${entry}" is a consumer email provider. Domain-blocking it would block ALL ${entry} addresses. Please switch to "Single Email" and enter the specific address instead.`);
        return;
      }
    } else {
      entryType = 'email';
      if (!emailRegex.test(entry)) {
        setMessage('Please enter a valid email address.');
        return;
      }
    }
    
    try {
      if (!isValidBlacklistDocId(entry)) {
        setMessage('Invalid entry – contains forbidden characters or is too long.');
        return;
      }
      const blacklistDocRef = doc(firestore, `World/${userId}/blacklist`, entry);
      // Single getDoc is much cheaper than scanning the full collection that
      // we no longer keep in memory.
      const existing = await getDoc(blacklistDocRef);
      if (existing.exists()) {
        setMessage(`This ${entryType} is already in the blacklist.`);
        return;
      }
      await setDoc(blacklistDocRef, {
        email: entry,
        addedAt: Date.now(),
        source: 'manual',
        type: entryType
      });

      // Bridge: if specific email (not domain), find contact and set globalOptOut
      if (entryType === 'email') {
        try {
          const dbRef = collection(firestore, `World/${userId}/db`);
          const q = query(dbRef, where('lead_email', '==', entry));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const contactDoc = snap.docs[0];
            await updateDoc(contactDoc.ref, {
              globalOptOut: true,
              globalOptOutAt: Date.now(),
              globalOptOutSource: 'blacklist_manual',
            });
          }
        } catch (_) { /* non-critical */ }
      }
      
      setManualEmail('');
      setMessage(`${entryType === 'domain' ? 'Domain' : 'Email'} added to blacklist successfully.`);
      refreshMeta();

      // Clear message nach 2 Sekunden
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('Error adding entry:', error);
      setMessage('Error adding entry. Please try again.');
    }
  };

  // Standard Provider zur Blacklist hinzufügen
  const handleAddStandardProviders = async () => {
    if (selectedProviders.length === 0) {
      setMessage('Please select at least one provider.');
      return;
    }

    setAddingProviders(true);

    try {
      const baseTimestamp = Date.now();
      // alreadyBlockedProviders is loaded server-side via the consumer-block
      // query — accurate even though we only keep 20 entries in memory.
      const blockedSet = new Set(alreadyBlockedProviders);
      const providersToAdd = selectedProviders.filter(p =>
        !blockedSet.has(p) && isValidBlacklistDocId(p)
      );

      if (providersToAdd.length === 0) {
        setMessage('All selected providers are already in the blacklist.');
        setAddingProviders(false);
        return;
      }

      // Standard provider list is always small (<50), one batch is enough.
      const batch = writeBatch(firestore);
      providersToAdd.forEach((provider, idx) => {
        const blacklistDocRef = doc(firestore, `World/${userId}/blacklist/${provider}`);
        batch.set(blacklistDocRef, {
          email: provider,
          addedAt: baseTimestamp + idx,
          source: 'standard_providers',
          type: 'domain',
        });
      });
      await batch.commit();

      setMessage(`Successfully added ${providersToAdd.length} consumer email providers to blacklist.`);
      setShowProviderModal(false);
      setSelectedProviders([]);
      refreshMeta();

      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding providers:', error);
      setMessage('Error adding providers. Please try again.');
    } finally {
      setAddingProviders(false);
    }
  };

  // Toggle Provider Auswahl
  const toggleProvider = (provider) => {
    setSelectedProviders(prev => 
      prev.includes(provider) 
        ? prev.filter(p => p !== provider)
        : [...prev, provider]
    );
  };

  // Alle Provider auswählen/abwählen
  const toggleAllProviders = () => {
    if (selectedProviders.length === availableQuickBlockProviders.length) {
      setSelectedProviders([]);
    } else {
      setSelectedProviders(availableQuickBlockProviders);
    }
  };

  // Email/Domain aus Blacklist entfernen
  const handleRemoveEntry = async (entryId, entry) => {
    const isD = entry.startsWith('@');
    if (!window.confirm(`Remove "${entry}" from blacklist?`)) {
      return;
    }

    try {
      const blacklistDocRef = doc(firestore, `World/${userId}/blacklist`, entryId);
      // Try to find metadata in any of the locally cached lists. If the entry
      // isn't there (older page, search-only result) we just skip the bridge —
      // the deletion still works.
      const blData =
        pagedEntries.find(item => item.id === entryId) ||
        (searchResults || []).find(item => item.id === entryId) ||
        allConsumerBlocks.find(item => item.id === entryId);
      await deleteDoc(blacklistDocRef);

      // Bridge: if contact-linked email, also remove globalOptOut
      if (!isD && blData?.contactId) {
        try {
          await updateDoc(doc(firestore, `World/${userId}/db/${blData.contactId}`), {
            globalOptOut: false,
            globalOptOutRemovedAt: Date.now(),
            lead_contacted: 'Lead',
            lead_status: 'Lead',
          });
        } catch (_) {}
      }

      // If we removed the currently-displayed search hit, drop it from results
      // so it disappears from the UI immediately.
      if (searchResults) {
        setSearchResults(searchResults.filter(r => r.id !== entryId));
      }
      refreshMeta();

      setMessage(`${isD ? 'Domain' : 'Email'} removed from blacklist.`);

      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('Error removing entry:', error);
      setMessage('Error removing entry. Please try again.');
    }
  };

  // Alle Einträge löschen
  const handleClearAll = async () => {
    if (!window.confirm(`Are you sure you want to remove all ${counts.total.toLocaleString()} entries from the blacklist? This action cannot be undone.`)) {
      return;
    }

    setClearing(true);
    setMessage('Removing entries...');

    try {
      const blacklistCollectionRef = collection(firestore, `World/${userId}/blacklist`);
      const snapshot = await getDocs(blacklistCollectionRef);

      const BATCH_SIZE = 500;
      const PARALLEL_BATCHES = 5;
      const docs = snapshot.docs;
      const total = docs.length;

      const chunks = [];
      for (let i = 0; i < total; i += BATCH_SIZE) {
        chunks.push(docs.slice(i, i + BATCH_SIZE));
      }

      setImportProgress({ done: 0, total });
      pauseSnapshotRef.current = true;

      let deleted = 0;
      try {
        await runWithConcurrency(chunks, PARALLEL_BATCHES, async (chunk) => {
          const batch = writeBatch(firestore);
          chunk.forEach(d => batch.delete(d.ref));
          await batch.commit();
          deleted += chunk.length;
          setImportProgress({ done: deleted, total });
        });
      } finally {
        pauseSnapshotRef.current = false;
        if (resubscribeRef.current) resubscribeRef.current();
      }

      setMessage(`Removed ${deleted} entries from blacklist.`);
      setImportProgress({ done: 0, total: 0 });
      setSearchResults(null);
      setSearchTerm('');
      refreshMeta();

      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('Error clearing blacklist:', error);
      setMessage('Error clearing blacklist. Please try again.');
      pauseSnapshotRef.current = false;
      if (resubscribeRef.current) resubscribeRef.current();
    } finally {
      setClearing(false);
    }
  };

  const handleCleanupConsumerDomains = async () => {
    if (consumerDomainBlocks.length === 0) return;

    const sample = consumerDomainBlocks.slice(0, 10).map(e => e.email).join(', ');
    const more = consumerDomainBlocks.length > 10 ? ` (+${consumerDomainBlocks.length - 10} more)` : '';
    if (!window.confirm(
      `Remove ${consumerDomainBlocks.length} consumer-provider domain block${consumerDomainBlocks.length > 1 ? 's' : ''}?\n\n` +
      `${sample}${more}\n\n` +
      `These currently block ALL addresses on those providers. ` +
      `Removing them lets you contact people on Gmail/GMX/Yahoo etc. again. ` +
      `Individually blacklisted addresses (e.g. specific@gmail.com) stay blocked.`
    )) return;

    setClearing(true);
    setMessage('Removing consumer-provider domain blocks...');
    pauseSnapshotRef.current = true;
    try {
      const batch = writeBatch(firestore);
      consumerDomainBlocks.forEach(item => {
        batch.delete(doc(firestore, `World/${userId}/blacklist`, item.id));
      });
      await batch.commit();
      setMessage(`Removed ${consumerDomainBlocks.length} consumer-provider domain block${consumerDomainBlocks.length > 1 ? 's' : ''}. Gmail, GMX, Yahoo etc. are reachable again.`);
      refreshMeta();
      setTimeout(() => setMessage(''), 5000);
    } catch (e) {
      console.error('Cleanup error:', e);
      setMessage('Error removing entries. Please try again.');
    } finally {
      pauseSnapshotRef.current = false;
      if (resubscribeRef.current) resubscribeRef.current();
      setClearing(false);
    }
  };

  // What goes on screen: search results when in search mode, otherwise the
  // currently-loaded paginated window.
  const isSearchMode = searchResults !== null;
  const displayEntries = isSearchMode ? searchResults : pagedEntries;
  const domainEntries = displayEntries.filter(item => item.email.startsWith('@'));
  const emailEntries = displayEntries.filter(item => !item.email.startsWith('@'));

  // Hero stats come from getCountFromServer — accurate even with paginated
  // loading.
  const domainCount = counts.domains;
  const emailCount = counts.emails;
  const totalCount = counts.total;
  const canLoadMore = !isSearchMode && pagedEntries.length >= pageLimit && pagedEntries.length < totalCount;

  return (
    <div className="bl">
      <header className="bl-page-head">
        <div className="bl-page-head-main">
          <div>
            <h3 className="bl-page-title">BlackList</h3>
            <p className="bl-page-sub">
              Blocked addresses and domains are filtered from campaigns and imports automatically.
            </p>
          </div>
        </div>
        <div className="bl-page-stats">
          <span className="bl-stat">
            <span className="bl-stat-num">{domainCount.toLocaleString()}</span>
            <span className="bl-stat-label">domains</span>
          </span>
          <span className="bl-stat">
            <span className="bl-stat-num">{emailCount.toLocaleString()}</span>
            <span className="bl-stat-label">emails</span>
          </span>
          <span className="bl-stat">
            <span className="bl-stat-num">{totalCount.toLocaleString()}</span>
            <span className="bl-stat-label">total</span>
          </span>
        </div>
      </header>

      {consumerDomainBlocks.length > 0 && (
        <section className="bl-section bl-warn">
          <span className="bl-icon" aria-hidden>
            <i className="fa fa-exclamation-triangle" />
          </span>
          <div className="bl-warn-text">
            <strong>
              {consumerDomainBlocks.length} consumer-provider domain
              {consumerDomainBlocks.length === 1 ? ' is' : 's are'} blocked entirely
            </strong>
            <p>
              {consumerDomainBlocks.slice(0, 6).map(e => e.email).join(', ')}
              {consumerDomainBlocks.length > 6 ? ` (+${consumerDomainBlocks.length - 6} more)` : ''}
              {' — this currently blocks every address on '}
              {consumerDomainBlocks.length === 1 ? 'that provider' : 'those providers'}.
              If that wasn't intentional, remove these blocks to reach Gmail/GMX/Yahoo recipients
              again. Specific addresses you blacklisted individually stay blocked. To intentionally
              block all consumer providers, use "Block Consumer Providers" in Quick Block.
            </p>
          </div>
          <button
            type="button"
            onClick={handleCleanupConsumerDomains}
            disabled={clearing}
            className="altBtn altBtn--petrol"
          >
            {clearing ? <><i className="fa fa-spinner fa-spin" /> Removing...</> : <><i className="fa fa-eraser" /> Remove these blocks</>}
          </button>
        </section>
      )}

      <div className="bl-cols">
        <section className="bl-section bl-section--fill">
          <header className="bl-section-head">
            <div>
              <span className="bl-section-eyebrow">Add</span>
              <h4 className="bl-section-title">Block an address</h4>
              <p className="bl-section-sub">
                One email or an entire domain. Domain blocks catch every address on that host.
              </p>
            </div>
          </header>
          <div className="bl-add-body">
          <div className="bl-toggle">
            <button
              type="button"
              className={`bl-toggle-btn ${blockType === 'email' ? 'active' : ''}`}
              onClick={() => setBlockType('email')}
            >
              <i className="far fa-envelope" /> Single Email
            </button>
            <button
              type="button"
              className={`bl-toggle-btn ${blockType === 'domain' ? 'active' : ''}`}
              onClick={() => setBlockType('domain')}
            >
              <i className="fa fa-globe" /> Entire Domain
            </button>
          </div>
          <div className="bl-input-row">
            <input
              type="text"
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
              placeholder={blockType === 'domain' ? '@example.com' : 'email@example.com'}
              className="bl-input"
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAddManualEntry();
              }}
            />
            <button type="button" onClick={handleAddManualEntry} className="altBtn altBtn--save">
              <i className="fa fa-plus" /> Add
            </button>
          </div>
          <p className="bl-hint">
            {blockType === 'domain'
              ? 'All emails ending with this domain will be blocked.'
              : 'Only this specific email address will be blocked.'}
          </p>
          </div>
        </section>

        <div className="bl-col-stack">
        <section className="bl-section">
          <header className="bl-section-head">
            <div>
              <span className="bl-section-eyebrow">Quick block</span>
              <h4 className="bl-section-title">Consumer providers</h4>
              <p className="bl-section-sub">
                Instantly block Gmail, GMX, Web.de, Yahoo and other private inboxes from B2B outreach.
              </p>
            </div>
          </header>
          <button
            type="button"
            onClick={() => {
              setSelectedProviders([]);
              setShowProviderModal(true);
            }}
            className="altBtn altBtn--petrol"
          >
            <i className="fa fa-shield" />
            Block Consumer Providers
          </button>
        </section>

        <section className="bl-section">
          <header className="bl-section-head">
            <div>
              <span className="bl-section-eyebrow">Import</span>
              <h4 className="bl-section-title">From a file</h4>
              <p className="bl-section-sub">
                CSV or TXT — we detect the email column.
              </p>
            </div>
          </header>
        
        {step === 1 && (
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".csv,.txt"
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="bl-dropzone"
              onClick={() => fileInputRef.current?.click()}
            >
              <span className="bl-icon" aria-hidden>
                <i className="far fa-file-lines" />
              </span>
              <span className="bl-dropzone-copy">
                <span className="bl-dropzone-cta">Choose CSV or TXT</span>
                <span className="bl-dropzone-hint">Column detected automatically</span>
              </span>
            </button>
          </div>
        )}

        {step === 2 && (
          <>
            <div className="bl-filename">
              <i className="far fa-file" /> {fileName}
            </div>
            
            <div>
              <label className="bl-field-label">Email column</label>
              <select
                value={emailColumnIndex !== null ? emailColumnIndex : ''}
                onChange={(e) => setEmailColumnIndex(e.target.value ? parseInt(e.target.value) : null)}
                className="bl-select"
              >
                <option value="">Select column...</option>
                {columns.map((col, index) => (
                  <option key={index} value={index}>
                    {col} {index === emailColumnIndex ? '(auto-detected)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="bl-field-label">Block mode</label>
              <div className="bl-toggle">
                <button
                  type="button"
                  className={`bl-toggle-btn ${importBlockType === 'email' ? 'active' : ''}`}
                  onClick={() => setImportBlockType('email')}
                >
                  <i className="far fa-envelope" /> Single Emails
                </button>
                <button
                  type="button"
                  className={`bl-toggle-btn ${importBlockType === 'domain' ? 'active' : ''}`}
                  onClick={() => setImportBlockType('domain')}
                >
                  <i className="fa fa-globe" /> Entire Domains
                </button>
              </div>
              <p className="bl-hint">
                {importBlockType === 'domain'
                  ? 'Extracts the domain from each email (e.g. john@example.com → @example.com).'
                  : 'Each email address will be blocked individually.'}
              </p>
            </div>

            {emailColumnIndex !== null && (() => {
              // Live preview that mirrors the actual import logic — so users
              // can see upfront how rows will split between domain-level and
              // email-level blocks.
              let consumerCount = 0;
              let bareDomainPreview = 0;
              const previewDomains = new Set();
              const previewEmails = new Set();
              for (const row of csvRows) {
                const rawVal = row[emailColumnIndex]?.trim().toLowerCase();
                if (!rawVal) continue;
                const val = normalizeImportValue(rawVal);
                if (!val) continue;
                const isBareDomain = BARE_DOMAIN_RE.test(val);
                const isFullEmail = !isBareDomain && EMAIL_RE.test(val);
                if (!isBareDomain && !isFullEmail) continue;

                if (isBareDomain) {
                  previewDomains.add(val);
                  bareDomainPreview++;
                  continue;
                }

                const dom = extractDomainFromEmail(val);
                const isConsumer = dom && CONSUMER_PROVIDER_SET.has(dom);
                if (importBlockType === 'domain') {
                  if (isConsumer) {
                    previewEmails.add(val);
                    consumerCount++;
                  } else if (dom) {
                    previewDomains.add(dom);
                  }
                } else {
                  previewEmails.add(val);
                }
              }

              return (
                <div className="bl-preview">
                  <h5 className="bl-preview-title">Preview</h5>
                  <ul className="bl-preview-list">
                    {csvRows.slice(0, 5).map((row, idx) => {
                      const rawVal = row[emailColumnIndex]?.trim().toLowerCase() || '';
                      const val = normalizeImportValue(rawVal) || '';
                      const isBareDomain = BARE_DOMAIN_RE.test(val);
                      const isFullEmail = !isBareDomain && EMAIL_RE.test(val);
                      let displayValue;
                      if (isBareDomain) {
                        // Show the original value when it differed from the
                        // canonical form (e.g. "rekord.com" → "@rekord.com")
                        // so users can see the normalization upfront.
                        displayValue = rawVal && rawVal !== val
                          ? `${rawVal} → ${val}  (domain)`
                          : `${val}  (domain)`;
                      } else if (isFullEmail) {
                        const dom = extractDomainFromEmail(val);
                        const isConsumer = dom && CONSUMER_PROVIDER_SET.has(dom);
                        displayValue = importBlockType === 'domain'
                          ? (isConsumer ? `${val}  (consumer → email)` : dom)
                          : val;
                      } else {
                        displayValue = `${row[emailColumnIndex] || ''}  (skipped — not a valid email or domain)`;
                      }
                      return <li key={idx}><i className="fa fa-circle"></i>{displayValue}</li>;
                    })}
                  </ul>
                  <p className="bl-preview-meta">
                    {previewDomains.size > 0 && (
                      <>{previewDomains.size} domain{previewDomains.size === 1 ? '' : 's'}</>
                    )}
                    {previewDomains.size > 0 && previewEmails.size > 0 && ' + '}
                    {previewEmails.size > 0 && (
                      <>{previewEmails.size} email{previewEmails.size === 1 ? '' : 's'}</>
                    )}
                    {previewDomains.size === 0 && previewEmails.size === 0 && (
                      <span className="bl-preview-empty">No valid entries detected — check column selection.</span>
                    )}
                    {consumerCount > 0 && (
                      <> · <span className="bl-preview-note">Gmail/GMX/Yahoo etc. stay reachable for everyone else</span></>
                    )}
                    {bareDomainPreview > 0 && importBlockType === 'email' && (
                      <> · <span className="bl-preview-note">{bareDomainPreview} bare-domain row{bareDomainPreview === 1 ? '' : 's'} will be blocked at domain level</span></>
                    )}
                  </p>
                </div>
              );
            })()}

            <div className="bl-actions">
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setFile(null);
                  setFileName('');
                  setColumns([]);
                  setCsvRows([]);
                  setEmailColumnIndex(null);
                  setImportBlockType('email');
                }}
                className="altBtn altBtn--petrol"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={emailColumnIndex === null || importing}
                className="altBtn altBtn--save"
              >
                {importing ? (
                  <>
                    <i className="fa fa-spinner fa-spin" />{' '}
                    {importProgress.total > 0
                      ? `Importing ${importProgress.done.toLocaleString()} / ${importProgress.total.toLocaleString()}...`
                      : 'Preparing...'}
                  </>
                ) : (
                  <><i className="fa fa-download" /> Import {importBlockType === 'domain' ? 'Domains' : 'Emails'}</>
                )}
              </button>
            </div>
            {importing && importProgress.total > 0 && (
              <div className="bl-progress">
                <div
                  className="bl-progress-fill"
                  style={{ width: `${Math.min(100, (importProgress.done / importProgress.total) * 100)}%` }}
                />
              </div>
            )}
          </>
        )}

        {step === 3 && (
          <div className="bl-success">
            <span className="bl-icon" aria-hidden>
              <i className="fa fa-check" />
            </span>
            <h4>Import successful</h4>
          </div>
        )}
        </section>
        </div>
      </div>

      {message && (
        <div className={`bl-message ${message.includes('Error') || message.includes('Please') ? 'error' : 'success'}`}>
          <i className={`fa ${message.includes('Error') || message.includes('Please') ? 'fa-exclamation-circle' : 'fa-check-circle'}`} />
          {message}
        </div>
      )}

      <section className="bl-section">
        <header className="bl-section-head">
          <div>
            <span className="bl-section-eyebrow">List</span>
            <h4 className="bl-section-title">Blocked entries</h4>
            <p className="bl-section-sub">
              Search from the start of an address or domain. Load more to page through large lists.
            </p>
          </div>
          <div className="bl-section-head-actions">
            <span className="bl-section-badge">{totalCount.toLocaleString()} total</span>
            {totalCount > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                disabled={clearing}
                className="altBtn altBtn--petrol"
              >
                {clearing ? (
                  <>
                    <i className="fa fa-spinner fa-spin" />{' '}
                    {importProgress.total > 0
                      ? `Removing ${importProgress.done.toLocaleString()} / ${importProgress.total.toLocaleString()}...`
                      : 'Removing...'}
                  </>
                ) : (
                  <><i className="far fa-trash-can" /> Clear all</>
                )}
              </button>
            )}
          </div>
        </header>

        {totalCount > 0 && (
          <div className="bl-search">
            <i className={`fa ${searching ? 'fa-spinner fa-spin' : 'fa-search'} bl-search-icon`} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by start (e.g. tui finds tuija@… and @tui.de)..."
              className="bl-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="bl-search-clear"
                title="Clear search"
              >
                <i className="fa fa-times" />
              </button>
            )}
          </div>
        )}

        {isSearchMode && !searching && (
          <p className="bl-search-meta">
            {searchResults.length === SEARCH_LIMIT
              ? `Showing first ${SEARCH_LIMIT} matches — refine your search to narrow down.`
              : `${searchResults.length} match${searchResults.length === 1 ? '' : 'es'}`}
          </p>
        )}

        {domainEntries.length > 0 && (
          <div>
            <h4 className="bl-group-title">
              Blocked domains
              <span className="bl-group-count">{domainEntries.length}</span>
            </h4>
            <div className="bl-entries">
              {domainEntries.map((item) => (
                <div key={item.id} className="bl-entry">
                  <div className="bl-entry-main">
                    <span className="bl-entry-domain">
                      <i className="fa fa-globe" /> {item.email}
                    </span>
                  </div>
                  <div className="bl-entry-meta">
                    <span className="bl-entry-date">{item.addedAt ? new Date(item.addedAt).toLocaleDateString('de-DE') : '—'}</span>
                    <span className="bl-source">{sourceLabel(item.source)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveEntry(item.id, item.email)}
                    className="bl-remove"
                    title="Remove from blacklist"
                  >
                    <i className="fa fa-times" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {emailEntries.length > 0 && (
          <div>
            <h4 className="bl-group-title">
              Blocked emails
              <span className="bl-group-count">{emailEntries.length}</span>
            </h4>
            <div className="bl-entries">
              {emailEntries.map((item) => (
                <div key={item.id} className="bl-entry">
                  <div className="bl-entry-main">
                    <span className="bl-entry-email">{item.email}</span>
                  </div>
                  <div className="bl-entry-meta">
                    <span className="bl-entry-date">{item.addedAt ? new Date(item.addedAt).toLocaleDateString('de-DE') : '—'}</span>
                    <span className="bl-source">{sourceLabel(item.source)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveEntry(item.id, item.email)}
                    className="bl-remove"
                    title="Remove from blacklist"
                  >
                    <i className="fa fa-times" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {canLoadMore && (
          <button
            type="button"
            onClick={() => setPageLimit(prev => prev + PAGE_SIZE)}
            className="altBtn altBtn--petrol bl-load-more"
          >
            Load {Math.min(PAGE_SIZE, totalCount - pagedEntries.length).toLocaleString()} more
            ({pagedEntries.length.toLocaleString()} / {totalCount.toLocaleString()} shown)
          </button>
        )}

        {totalCount === 0 && (
          <div className="bl-empty">
            <span className="bl-icon bl-empty-icon" aria-hidden>
              <i className="fa fa-shield" />
            </span>
            <h5>No entries yet</h5>
            <p>Add emails or domains above to start building your blacklist.</p>
          </div>
        )}

        {isSearchMode && !searching && searchResults.length === 0 && (
          <div className="bl-empty">
            <span className="bl-icon bl-empty-icon" aria-hidden>
              <i className="fa fa-search" />
            </span>
            <p>No entries start with "{searchTerm.trim()}".</p>
          </div>
        )}
      </section>

      {showProviderModal && createPortal(
        <div className="bl-modal-overlay" onClick={() => setShowProviderModal(false)}>
          <div className="bl-modal" onClick={e => e.stopPropagation()}>
            <div className="bl-modal-head">
              <div className="bl-modal-head-main">
                <span className="bl-icon" aria-hidden>
                  <i className="fa fa-shield" />
                </span>
                <div>
                  <h3>Block Consumer Providers</h3>
                  <p>Exclude private emails from your B2B campaigns</p>
                </div>
              </div>
              <button
                type="button"
                className="bl-modal-close"
                onClick={() => setShowProviderModal(false)}
              >
                <i className="fa fa-times" />
              </button>
            </div>

            <div className="bl-modal-body">
              <div className="bl-modal-toolbar">
                <button
                  type="button"
                  className="altBtn altBtn--petrol"
                  onClick={toggleAllProviders}
                >
                  {selectedProviders.length === availableQuickBlockProviders.length
                    ? 'Deselect all'
                    : 'Select all'}
                </button>
                <span className="bl-modal-count">
                  <strong>{selectedProviders.length}</strong> selected
                </span>
              </div>

              <div className="bl-provider-grid">
                {STANDARD_CONSUMER_PROVIDERS.map(provider => {
                  const isBlocked = alreadyBlockedProviders.includes(provider);
                  const isSelected = selectedProviders.includes(provider);
                  return (
                    <label
                      key={provider}
                      className={`bl-provider ${isBlocked ? 'is-blocked' : ''} ${isSelected ? 'is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected || isBlocked}
                        disabled={isBlocked}
                        onChange={() => !isBlocked && toggleProvider(provider)}
                      />
                      <span className="bl-provider-name">{provider}</span>
                      {isBlocked && <span className="bl-provider-pill">Blocked</span>}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="bl-modal-foot">
              <button
                type="button"
                className="altBtn altBtn--petrol"
                onClick={() => setShowProviderModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="altBtn altBtn--save"
                onClick={handleAddStandardProviders}
                disabled={selectedProviders.length === 0 || addingProviders}
              >
                {addingProviders
                  ? <><i className="fa fa-spinner fa-spin" /> Adding...</>
                  : <><i className="fa fa-shield" /> Block {selectedProviders.length} Providers</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BlackList;
